import React, { useState, useEffect } from 'react';
import {
  RiLightbulbLine, RiTrophyLine, RiLeafLine, RiHeartLine,
  RiMapPin2Line, RiPhoneLine, RiMailLine, RiTimeLine,
  RiStarLine, RiShieldLine, RiHandCoinLine, RiToolsLine,
  RiCustomerService2Line, RiThumbUpLine, RiSpeedLine, RiGlobalLine,
} from 'react-icons/ri';
import { useApiCall } from '../hooks/useApiCall';
import '../styles/Aboutus.css';

// Map icon name strings (stored in DB) → actual components
const ICON_MAP = {
  RiLightbulbLine,
  RiTrophyLine,
  RiLeafLine,
  RiHeartLine,
  RiMapPin2Line,
  RiPhoneLine,
  RiMailLine,
  RiTimeLine,
  RiStarLine,
  RiShieldLine,
  RiHandCoinLine,
  RiToolsLine,
  RiCustomerService2Line,
  RiThumbUpLine,
  RiSpeedLine,
  RiGlobalLine,
};

function DynamicIcon({ name, className }) {
  const Icon = ICON_MAP[name];
  return Icon ? <Icon className={className} /> : null;
}

// ─── Section renderers ─────────────────────────────────────────────────────────
function HeroSection({ data }) {
  const hasText = !!(data.title || data.subtitle || data.description);
  const imgLeft = data.image_position === 'left';
  const imgH    = data.image_height || 400;
  const imgFit  = data.image_fit    || 'cover';

  if (!hasText) {
    return (
      <section className="hero-section hero-image-only">
        <div className="hero-image">
          <img src={data.image} alt="" style={{ height: imgH + 'px', objectFit: imgFit }} />
        </div>
      </section>
    );
  }

  return (
    <section className={"hero-section" + (imgLeft ? ' image-left' : '')}>
      <div className="hero-content">
        <h1>{data.title}</h1>
        <p className="hero-subtitle">{data.subtitle}</p>
        <p className="hero-description">{data.description}</p>
      </div>
      <div className="hero-image">
        <img src={data.image} alt={data.title} style={{ height: imgH + 'px', objectFit: imgFit }} />
      </div>
    </section>
  );
}

function MissionSection({ data }) {
  const imgLeft = data.image_position === 'left';
  const imgH    = data.image_height || 300;
  const imgFit  = data.image_fit    || 'cover';
  return (
    <section className={"mission-section" + (data.image ? (imgLeft ? ' has-image image-left' : ' has-image') : '')}>
      {data.image ? (
        <>
          <div className="mission-content">
            <h2>{data.title}</h2>
            <p>{data.body}</p>
          </div>
          <div className="mission-image">
            <img src={data.image} alt={data.title} style={{ height: imgH + 'px', objectFit: imgFit }} />
          </div>
        </>
      ) : (
        <div className="mission-content">
          <h2>{data.title}</h2>
          <p>{data.body}</p>
        </div>
      )}
    </section>
  );
}

function ValuesSection({ data }) {
  if (!data.items?.length) return null;
  return (
    <section className="values-section">
      <h2>{data.title}</h2>
      <div className="values-grid">
        {data.items.map((value, index) => (
          <div key={index} className="value-card">
            <div className="value-icon"><DynamicIcon name={value.icon} /></div>
            <h3>{value.title}</h3>
            <p>{value.description}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function TimelineSection({ data }) {
  if (!data.items?.length) return null;
  return (
    <section className="timeline-section">
      <h2>{data.title}</h2>
      <div className="timeline">
        {data.items.map((milestone, index) => (
          <div key={index} className="timeline-item">
            <div className="timeline-year">{milestone.year}</div>
            <div className="timeline-content">
              <h3>{milestone.title}</h3>
              <p>{milestone.description}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function StatsSection({ data }) {
  if (!data.items?.length) return null;
  return (
    <section className="stats-section">
      <div className="stats-grid">
        {data.items.map((stat, index) => (
          <div key={index} className="stat-item">
            <div className="stat-number">{stat.number}</div>
            <div className="stat-label">{stat.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ContactSection({ data }) {
  return (
    <section className="aboutus-contact-info-section">
      <h2>{data.title}</h2>
      <div className="aboutus-contact-grid">
        <div className="aboutus-contact-item">
          <RiMapPin2Line className="aboutus-contact-icon" />
          <div>
            <h3>Address</h3>
            <p>{data.address?.split('\n').map((line, i, arr) =>
              i < arr.length - 1 ? <React.Fragment key={i}>{line}<br /></React.Fragment> : line
            )}</p>
          </div>
        </div>
        <div className="aboutus-contact-item">
          <RiPhoneLine className="aboutus-contact-icon" />
          <div><h3>Phone</h3><p>{data.phone}</p></div>
        </div>
        <div className="aboutus-contact-item">
          <RiMailLine className="aboutus-contact-icon" />
          <div><h3>Email</h3><p>{data.email}</p></div>
        </div>
        <div className="aboutus-contact-item">
          <RiTimeLine className="aboutus-contact-icon" />
          <div><h3>Hours</h3><p>{data.hours}</p></div>
        </div>
      </div>
    </section>
  );
}

function CtaSection({ data }) {
  return (
    <section className="cta-section">
      <div className="cta-content">
        <h2>{data.title}</h2>
        <p>{data.description}</p>
        <div className="cta-buttons">
          {data.btn1_label && (
            <button className="btn-primary" onClick={() => window.location.href = data.btn1_url}>
              {data.btn1_label}
            </button>
          )}
          {data.btn2_label && (
            <button className="btn-secondary" onClick={() => window.location.href = data.btn2_url}>
              {data.btn2_label}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

const SECTION_RENDERERS = {
  hero: HeroSection,
  mission: MissionSection,
  values: ValuesSection,
  timeline: TimelineSection,
  stats: StatsSection,
  contact: ContactSection,
  cta: CtaSection,
};

const Aboutus = () => {
  const apiCall = useApiCall();
  const [blocks, setBlocks] = useState(null);

  useEffect(() => {
    apiCall('aboutus/get', 'POST', {}, null, null).then((res) => {
      if (res?.success) setBlocks(res.data?.content);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!blocks || blocks.length === 0) return null;

  return (
    <div className="aboutus-page">
      <div className="aboutus-container">
        {blocks
          .filter(block => block.enabled !== false)
          .map(block => {
            const Component = SECTION_RENDERERS[block.type];
            return Component ? <Component key={block.id} data={block.data || {}} /> : null;
          })
        }
      </div>
    </div>
  );
};

export default Aboutus;
